import React, { useEffect, useState } from 'react';
import { Col, Row, FormInput } from '@snsw/react-component-library';
import {
  isEmpty,
  isResidentialAddress,
  isStreetAddressValid,
  isValidEmail,
  validateExt,
} from 'shared/utils/Validation';
import PropTypes from 'prop-types';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';
import _ from 'lodash';
import { PhoneNumber } from 'shared/utils/PhoneNumber';
import { NewContactNumber } from 'shared/components/contactNumber/NewContactNumber';
import { AddressWithDuplicate } from 'shared/components/address/AddressWithDuplicate';
import { IndividualOngoingContactFields } from './IndividualOngoingContactFields';

const IndividualHolderDetailFields = React.forwardRef((props, ref) => {
  const { value: holderDetails = {}, onErrorChange = () => {} } = props;
  const {
    address: savedResidentialAddress = {},
    mailingAddress: savedMailingAddress = {},
    isSameAddress: savedIsSameAddress = true,
    firstName: savedFirstName = '',
    middleName: savedMiddleName = '',
    lastName: savedLastName = '',
    emailAddress: savedEmailAddress = '',
    phoneNumber: savedPhoneNumber = {},
  } = holderDetails;

  const [isResidentialAddressValid, setIsResidentialAddressValid] = useState(false);
  const [isMailingAddressValid, setIsMailingAddressValid] = useState(false);
  const [isAddressLoading, setAddressLoading] = useState(false);
  const [residentAddress, setResidentAddress] = useState(_.cloneDeep(savedResidentialAddress));
  const [mailingAddress, setMailingAddress] = useState(_.cloneDeep(savedMailingAddress));
  const [isSameAddress, setIsSameAddress] = useState(savedIsSameAddress);

  const [firstName, setFirstName] = useState(savedFirstName);
  const [middleName, setMiddleName] = useState(savedMiddleName);
  const [lastName, setLastName] = useState(savedLastName);
  const [emailAddress, setEmailAddress] = useState(savedEmailAddress);
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber);
  const [showErrors, setShowErrors] = useState(false);
  const [ongoingContact, setOngoingContact] = useState(holderDetails?.ongoingContact);

  const [parentErrors, setParentErrors] = useState([]);
  const [ongoingContactErrors, setOngoingContactErrors] = useState([]);

  const [errors, setErrors] = useState({});

  const ongoingContactRef = React.useRef();

  const validators = [
    {
      fieldName: 'firstName',
      fieldValue: firstName,
      isValid: (v) => !isEmpty(v) || !isEmpty(lastName),
      errorMessage: () => 'Name is required.',
    },
    {
      fieldName: 'lastName',
      fieldValue: lastName,
      isValid: (v) => !isEmpty(v) || !isEmpty(firstName),
      errorMessage: () => 'Name is required.',
    },
    {
      fieldName: 'emailAddress',
      fieldValue: emailAddress,
      isValid: isValidEmail,
      errorMessage: (v) => (isEmpty(v) ? 'Email address is required.' : 'Enter a valid email address.'),
    },
    {
      fieldName: 'phoneNumber',
      fieldValue: phoneNumber,
      isValid: (v) => !!v?.formattedNumber,
      errorMessage: (v) => (isEmpty(v?.raw?.number) ? 'Phone number is required.' : 'Enter valid phone number.'),
    },
    {
      fieldName: 'address',
      fieldValue: null,
      isValid: () => isResidentialAddressValid && isResidentialAddress(residentAddress),
      errorMessage: () =>
        isResidentialAddressValid ? 'Enter a valid residential address.' : 'Residential address is required.',
    },
    {
      fieldName: 'address',
      fieldValue: null,
      isValid: () => isStreetAddressValid(residentAddress, 64),
      errorMessage: () => 'The length of the street address cannot be more than 64 characters.',
    },
    {
      fieldName: 'mailingAddress',
      fieldValue: null,
      isValid: () => isSameAddress || (isMailingAddressValid && isStreetAddressValid(mailingAddress, 64)),
      errorMessage: () =>
        isMailingAddressValid
          ? 'The length of the street address cannot be more than 64 characters.'
          : 'Mailing address is required',
    },
  ];

  const validateAll = () => {
    let isOngoingValid = ongoingContactRef.current?.validate();

    let isCurrentComponentValid = validateExt(validators, setErrors, setParentErrors);

    setShowErrors(!isCurrentComponentValid);

    return isOngoingValid && isCurrentComponentValid;
  };

  if (ref) {
    ref.current = {
      validate: () => validateAll(),
    };
  }

  useEffect(() => {
    props.amIRegistrationHolder && props.applicantEmail
      ? setEmailAddress(props.applicantEmail)
      : setEmailAddress(savedEmailAddress);
  }, [props.amIRegistrationHolder, props.applicantEmail]);

  useEffect(() => {
    if (props.onChange) {
      let mydetails = {
        firstName,
        middleName,
        lastName,
        emailAddress,
        phoneNumber,
        address: residentAddress,
        isSameAddress,
        mailingAddress,
        ongoingContact,
      };
      props.onChange(mydetails);
    }

    if (showErrors) validateAll();
  }, [
    firstName,
    middleName,
    lastName,
    emailAddress,
    phoneNumber,
    residentAddress,
    mailingAddress,
    isSameAddress,
    isMailingAddressValid,
    isResidentialAddressValid,
    ongoingContact,
  ]);

  useEffect(() => {
    let errorSummary = parentErrors;

    onErrorChange(errorSummary.concat(ongoingContactErrors));
  }, [parentErrors, isMailingAddressValid, isResidentialAddressValid, ongoingContactErrors]);

  return (
    <>
      <Row style={{ marginTop: 20 }}>
        <Col span={4}>
          <FormInput
            id="firstName"
            label={ItemRegistrationHolderPage.holderFirstNameLabel}
            name="firstName"
            hasError={showErrors && errors?.firstName?.invalid}
            errorMessage={errors?.firstName?.errorMessage}
            onChange={(e, { value }) => setFirstName(value)}
            value={firstName}
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <FormInput
            id="middleName"
            label={ItemRegistrationHolderPage.holderMiddleNameLabel}
            name="middleName"
            onChange={(e, { value }) => setMiddleName(value)}
            value={middleName}
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <FormInput
            id="lastName"
            label={ItemRegistrationHolderPage.holderLastNameLabel}
            name="lastName"
            hasError={showErrors && errors?.lastName?.invalid}
            errorMessage={errors?.lastName?.errorMessage}
            onChange={(e, { value }) => setLastName(value)}
            value={lastName}
          />
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          <FormInput
            id="emailAddress"
            label={ItemRegistrationHolderPage.emailAddressLabel}
            name="Email address"
            type="email"
            hasError={showErrors && errors?.emailAddress?.invalid}
            errorMessage={errors?.emailAddress?.errorMessage}
            onChange={(e, { value }) => setEmailAddress(value)}
            value={emailAddress}
            disabled={props.amIRegistrationHolder}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={9}>
          <NewContactNumber
            id="phoneNumber"
            value={phoneNumber?.raw?.number}
            label={ItemRegistrationHolderPage.phoneNumberLabel}
            helpMessage={ItemRegistrationHolderPage.phoneNumberHelpText}
            errorMessage={errors?.phoneNumber?.errorMessage}
            hasError={showErrors && errors?.phoneNumber?.invalid}
            onChange={(v) => setPhoneNumber(new PhoneNumber('', v, undefined, true))}
            allowNonNumeric={false}
            inputWidth={'lg'}
          />
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <div id="address">
            <AddressWithDuplicate
              autoValidate
              firstAddressLabel={ItemRegistrationHolderPage.residentialAddressLabel}
              firstAddressMessage="Start typing to search"
              secondAddressLabel={ItemRegistrationHolderPage.mailingAddressLabel}
              secondAddressMessage="Start typing to search"
              baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}`}
              showErrors={{
                firstAddress: showErrors && !isResidentialAddressValid,
                secondAddress: showErrors && !isMailingAddressValid,
              }}
              onChange={(data) => {
                setIsSameAddress(data.isSameAddress);
                setResidentAddress(data.firstAddress);
                setMailingAddress(data.secondAddress);
              }}
              onLoading={(data) => {
                setAddressLoading(!!(data.isFirstAddressValidating || data.isSecondAddressValidating));
              }}
              onValidationChange={(v) => {
                setIsResidentialAddressValid(v.firstAddressValid);
                setIsMailingAddressValid(v.secondAddressValid);
              }}
              value={{
                isSameAddress,
                firstAddress: residentAddress,
                secondAddress: mailingAddress,
              }}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <IndividualOngoingContactFields
            ref={ongoingContactRef}
            onChange={setOngoingContact}
            value={holderDetails?.ongoingContact}
            onErrorChange={setOngoingContactErrors}
            amIRegistrationHolder={props.amIRegistrationHolder}
          />
        </Col>
      </Row>
    </>
  );
});

IndividualHolderDetailFields.displayName = 'IndividualHolderDetailFields';

IndividualHolderDetailFields.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
  amIRegistrationHolder: PropTypes.bool,
  applicantEmail: PropTypes.string,
};

IndividualHolderDetailFields.defaultProps = {
  onChange: null,
  value: null,
};

export { IndividualHolderDetailFields };
